import React, { useState, useEffect } from 'react';
import { Calendar, Activity, Layers, TrendingUp } from 'lucide-react';

const MONTHS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];

const ColorCell = ({ value }) => {
    if (value === undefined || value === null) return <td className="p-3 text-center text-slate-200 text-xs">-</td>;
    
    // Intensité de la couleur (max saturé à 10%)
    const intensity = Math.min(Math.abs(value) / 10, 1); 
    const isPositive = value >= 0;
    
    // Nuances dynamiques
    const bgStyle = {
        backgroundColor: isPositive 
            ? `rgba(16, 185, 129, ${0.1 + (intensity * 0.5)})` // Emerald
            : `rgba(239, 68, 68, ${0.1 + (intensity * 0.5)})`  // Rose/Red
    };

    return (
        <td className="p-3 text-center text-sm font-bold border border-slate-100 transition-all hover:brightness-95" style={bgStyle}>
            <span className={isPositive ? "text-emerald-900" : "text-rose-900"}>
                {value > 0 ? '+' : ''}{value}%
            </span>
        </td>
    );
};

const YearRow = ({ year, data }) => {
    const total = data['TOTAL'];
    return (
        <tr>
            <td className="p-3 font-black text-slate-700 bg-slate-50 border-r border-slate-200 sticky left-0 z-10">{year}</td>
            {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                <ColorCell key={m} value={data[m]} />
            ))}
            <td className="p-3 font-black border-l border-slate-200 bg-slate-50 text-center">
                <span className={`px-2 py-1 rounded ${total >= 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
                    {total > 0 ? '+' : ''}{total}%
                </span>
            </td>
        </tr>
    );
};

const MatrixTable = ({ title, data, icon: Icon, colorClass = "text-slate-600" }) => {
    if (!data || Object.keys(data).length === 0) return null;
    
    // Trier les années (Décroissant)
    const years = Object.keys(data).sort((a, b) => b - a);

    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-8">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2 bg-slate-50/50">
                <div className={`p-2 bg-white rounded-lg border border-slate-200 ${colorClass}`}>
                    <Icon size={18} />
                </div>
                <h3 className="font-bold text-slate-800">{title}</h3>
            </div>
            
            <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                    <thead>
                        <tr className="text-xs uppercase text-slate-400 border-b border-slate-200">
                            <th className="p-3 bg-slate-50 border-r border-slate-200 text-left sticky left-0 z-10 w-20">Année</th>
                            {MONTHS.map(m => <th key={m} className="p-3 font-bold bg-white min-w-[50px]">{m}</th>)}
                            <th className="p-3 bg-slate-50 border-l border-slate-200 w-24">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {years.map(y => (
                            <YearRow key={y} year={y} data={data[y]} />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const PerformanceMatrix = ({ portfolioId }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            if(!portfolioId) return;
            setLoading(true);
            try {
                const token = localStorage.getItem('token');
                const res = await fetch(`/api/portfolio/${portfolioId}/performance-matrix`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    setData(await res.json());
                }
            } catch (error) {
                console.error("Perf Matrix Error", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [portfolioId]);

    if (loading) return <div className="p-12 text-center text-slate-400 animate-pulse bg-white rounded-2xl border border-dashed mt-6">Calcul des rendements mensuels en cours...</div>;
    
    if (!data || !data.portfolio || Object.keys(data.portfolio).length === 0) return null;

    return (
        <div className="space-y-6 mt-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="flex items-center gap-2 mb-4 border-b border-slate-200 pb-4">
                <Calendar className="text-indigo-600" size={24} />
                <h2 className="text-xl font-black text-slate-900">Rendements Mensuels</h2>
            </div>

            {/* MON PORTEFEUILLE */}
            <MatrixTable title="Mon Portefeuille" data={data.portfolio} icon={Layers} colorClass="text-indigo-600" />
            
            {/* BENCHMARKS (Maintenant en pleine largeur, l'un sous l'autre) */}
            <div className="space-y-8">
                <MatrixTable title="S&P 500 (Benchmark)" data={data.sp500} icon={TrendingUp} colorClass="text-emerald-600" />
                <MatrixTable title="Nasdaq (Benchmark)" data={data.nasdaq} icon={Activity} colorClass="text-blue-600" />
            </div>
            
            <p className="text-center text-xs text-slate-400 italic mt-4">
                Méthode : Time-Weighted Return (TWR) pour neutraliser l'impact des dépôts/retraits sur la performance.
            </p>
        </div>
    );
};

export default PerformanceMatrix;
